import { useSearch } from "@tanstack/react-router";
import { useGetIntegrationQuery } from "api/TenantSlug/Integration/hooks";
import { ModifyIntegrationDrawer } from "components/Drawers/ModifyIntegrationDrawer/ModifyIntegrationDrawer.tsx";
import { ApiToolGeneralForm } from "components/Forms/ApiToolGeneralForm/ApiToolGeneralForm.tsx";
import { QueryState } from "components/UI/QueryState/QueryState.tsx";
import { TenantPage } from "components/UI/TenantPage/TenantPage.tsx";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import { PermissionEnum, RoleEnum } from "types/enums.ts";
import { DrawerId, FormId } from "types/models.ts";
import { createTenantFileRoute } from "utils/createTenantFileRoute.ts";
import { getToolScenarioIntegrationHeaderItems } from "utils/tenantHeaderItems.ts";

interface SearchParams {
    [`modifyIntegration-id`]: string;
}
export const Route = createTenantFileRoute(
    "/_authenticated/$tenantSlug/_layout/tool/_layout/scenario/_layout/integration/_layout/$integrationId/_layout/general/"
)({
    component: IntegrationGeneralPage,
    staticData: {
        permissions: [PermissionEnum.ToolIntegrationGeneral],
        showInSideMenu: false,
        label: "pages.tool.scenario.integration.general.title",
    },
    loader: ({ context: { t } }) => {
        return {
            breadcrumb: t("pages.tool.scenario.integration.general.title"),
        };
    },
});

function IntegrationGeneralPage() {
    const { integrationId } = Route.useParams();
    const { data, isLoading, isError } = useGetIntegrationQuery(integrationId, {
        filter: { include: "offers,country,category,labels" },
    });
    const integration = data?.integration;
    const userUtils = useUser();
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const formId = FormId.ApiToolGeneralForm;
    const drawerId = DrawerId.ModifyIntegration;
    const permissionsUtils = usePermission();

    if (!integration) {
        return (
            <QueryState isLoading={isLoading} isError={isError || !isLoading} errorKey="errors.loading_integration" />
        );
    }

    return (
        <TenantPage
            headerNavigationItems={() => getToolScenarioIntegrationHeaderItems()}
            isEdit
            saveFormId={
                userUtils
                    ?.getActiveTenant()
                    ?.userRoles?.some((role) => [RoleEnum.AdminFree, RoleEnum.AdminBeast].includes(role.name))
                    ? formId
                    : null
            }
        >
            <ApiToolGeneralForm key={integrationId} integration={integration} />
            {searchParams[`${drawerId}-id`] &&
                permissionsUtils.hasPermission(PermissionEnum.ToolIntegrationExportView) && <ModifyIntegrationDrawer />}
        </TenantPage>
    );
}
